import {
  ADMIN_ROLE,
  SUPERADMIN_ROLE,
  type RequiredAuth,
  hasPermission,
} from '@carsfinders/auth';
import {
  BarChart3,
  Calendar,
  Gavel,
  KeyRound,
  Percent,
  Server,
  Store,
  LayoutDashboard,
  MoreHorizontal,
  Settings,
  Smartphone,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import { t } from '../lib/i18n';

/**
 * The three sidebar sections the artboard draws (`Redesign-D-Admin`, the `NAV`
 * array in its script block): Робота — the daily queue, Перевірки — things an
 * operator approves, Довідники — reference data that changes rarely.
 */
export type NavGroup = 'work' | 'checks' | 'reference';

/** Same shape as web navItems: label + href + required auth. */
export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  auth?: RequiredAuth;
  /** The sidebar section the row sits under. */
  group: NavGroup;
  /**
   * Which count, if any, the artboard draws as a pill on this row. The shell
   * resolves it from the live dashboard metrics — the artboard's numbers are
   * mock, the badge itself is not.
   */
  badgeKey?: 'documents' | 'deals' | 'dealers' | 'marketplace' | 'relay' | 'finance';
}

/** Section order and headings, top to bottom. */
export const NAV_GROUPS: readonly { readonly id: NavGroup; readonly label: string }[] = [
  { id: 'work', label: t('admin.nav.group.work') },
  { id: 'checks', label: t('admin.nav.group.checks') },
  { id: 'reference', label: t('admin.nav.group.reference') },
];

/**
 * Sidebar order is the artboard's order, not the order the routes were built
 * in: Робота (Огляд, Угоди, Клієнти, Фінанси, Аналітика) · Перевірки (Документи, Заявки
 * дилерів, Маркетплейс) · Довідники (Розклад, Ключі фіду, Налаштування).
 *
 * «Угоди» is the one queue that replaced «Ставки» and «Виграні авто» (admin
 * deals plan, phase 8); the old addresses redirect into its tabs.
 *
 * One row the artboard does not draw, deliberately: «Тарифи» stays under
 * Довідники (owner decision f) — the tariff grid is edited here, not in a
 * deploy.
 *
 * «Маркетплейс», «Ключі фіду», «Налаштування» and «Тарифи» are for the
 * SUPERUSER only (owner decision 2026-10-13): `auth: SUPERADMIN_ROLE`, so a
 * plain admin's menu does not list them. The superuser also holds `admin`,
 * so every other row stays theirs too.
 */
export const NAV_ITEMS: NavItem[] = [
  // ── Робота ──
  {
    label: t('admin.nav.overview'),
    to: '/',
    icon: LayoutDashboard,
    auth: ADMIN_ROLE,
    group: 'work',
  },
  {
    label: t('admin.nav.deals'),
    to: '/deals',
    icon: Gavel,
    auth: ADMIN_ROLE,
    group: 'work',
    badgeKey: 'deals',
  },
  { label: t('admin.nav.clients'), to: '/clients', icon: Users, auth: ADMIN_ROLE, group: 'work' },
  /*
   * The finance badge is REAL: `GET /admin/finance/clients` answers it, and
   * `useNavBadges` folds the clients with something past due. The label comes
   * from the feature's own namespace rather than `admin.nav.*`: it is the same
   * word the screen's <h1> prints, so one key owns it.
   */
  {
    label: t('adminFinance.title'),
    to: '/finance',
    icon: Wallet,
    auth: ADMIN_ROLE,
    group: 'work',
    badgeKey: 'finance',
  },
  // Visitor analytics (admin-analytics plan, phase 5) — after «Фінанси».
  // SUPERUSER only (owner decision 2026-10-13, same as the four rows below).
  {
    label: t('admin.nav.analytics'),
    to: '/analytics',
    icon: BarChart3,
    auth: SUPERADMIN_ROLE,
    group: 'work',
  },
  // ── Перевірки ──
  // «Документи» and «Заявки дилерів» were taken off the menu (owner,
  // 2026-10-12): client documents are handled on the client card, and the
  // dealer account is withdrawn. Both routes still answer a direct link.
  {
    label: t('admin.nav.marketplace'),
    to: '/marketplace',
    icon: Store,
    auth: SUPERADMIN_ROLE,
    group: 'checks',
    badgeKey: 'marketplace',
  },
  // ── Довідники ──
  {
    label: t('admin.nav.schedule'),
    to: '/auction-schedule',
    icon: Calendar,
    auth: ADMIN_ROLE,
    group: 'reference',
  },
  {
    label: t('admin.nav.feedKeys'),
    to: '/feed-keys',
    icon: KeyRound,
    auth: SUPERADMIN_ROLE,
    group: 'reference',
  },
  {
    label: t('admin.nav.settings'),
    to: '/settings',
    icon: Settings,
    auth: SUPERADMIN_ROLE,
    group: 'reference',
  },
  // Owner decision (f): kept in the menu although the artboard omits it.
  {
    label: t('admin.nav.pricing'),
    to: '/pricing',
    icon: Percent,
    auth: SUPERADMIN_ROLE,
    group: 'reference',
  },
  // What the platform pays its providers (infra-costs, owner request 2026-10-14). SUPERUSER only.
  {
    label: t('admin.nav.infraCosts'),
    to: '/infra-costs',
    icon: Server,
    auth: SUPERADMIN_ROLE,
    group: 'reference',
  },
  // Where the admin app is signed in, with revoke / sign out everywhere
  // (admin-mobile-app phase 6, SC-10). SUPERUSER only.
  {
    label: t('admin.nav.mobileDevices'),
    to: '/mobile-devices',
    icon: Smartphone,
    auth: SUPERADMIN_ROLE,
    group: 'reference',
  },
];

/** One rendered sidebar section: its heading and the visible rows under it. */
export interface NavSection {
  group: NavGroup;
  label: string;
  items: NavItem[];
}

/**
 * Splits rows into the `NAV_GROUPS` sections, in section order, keeping each
 * row's relative order. A section with no visible row is dropped — a heading
 * over nothing reads like a broken menu.
 */
export function groupNavItems(items: readonly NavItem[]): NavSection[] {
  return NAV_GROUPS.map(({ id, label }) => ({
    group: id,
    label,
    items: items.filter((item) => item.group === id),
  })).filter((section) => section.items.length > 0);
}

/**
 * The mobile bar carries four destinations plus "Ще", which opens the same
 * drawer as the header's menu button (artboard `bottomNav`). Everything not in
 * this list is reachable only through that drawer — which is why the drawer
 * lists the full `NAV_ITEMS`, not a subset.
 */
export const BOTTOM_NAV_ROUTES = ['/', '/clients', '/finance', '/deals'] as const;

export const MORE_NAV_ITEM = { label: t('admin.nav.more'), icon: MoreHorizontal } as const;

/**
 * Filter nav items by the viewer's roles (shared @carsfinders/auth semantics).
 * The shell passes the real roles from `GET /users/me`; the default is a
 * plain admin, which never lists a superuser row — api-node still enforces
 * every call server-side (401/403 → AccessDenied).
 */
export function visibleNavItems(roles: string[] = [ADMIN_ROLE]): NavItem[] {
  return NAV_ITEMS.filter((item) => hasPermission(item.auth, roles));
}

/** The bottom bar's four, in the artboard's order, from the visible set. */
export function bottomNavItems(roles: string[] = [ADMIN_ROLE]): NavItem[] {
  const visible = visibleNavItems(roles);
  return BOTTOM_NAV_ROUTES.map((to) => visible.find((item) => item.to === to)).filter(
    (item): item is NavItem => item !== undefined,
  );
}
